import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { PlayArrow } from '@mui/icons-material';
import theme from '../../constants/theme';
import { FilledButtonStyled } from '../../components/button/filledButtonStyled';
import { OutlinedButtonStyled } from '../../components/button/outlinedButtonStyled';
import { useIsWhichSize } from '../../hooks/useIsWhichSize';
import { SmartrecruitersProps } from './props';
import { device } from '../../constants/devices';
import { t } from '../../utils/translate';
import Thumbnail from '../../assets/img/baywa_maxresdefault.jpeg';

export const Detail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isMobile } = useIsWhichSize();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [data, setData] = useState<SmartrecruitersProps | null>(null);
  const [showGDPRArray, setShowGDPRArray] = useState<boolean[]>([]);

  const isViewLoading = isLoading || !data;

  const regionName = new Intl.DisplayNames(['de'], { type: 'region' });

  const isYoutubeLink = (url: string): boolean => {
    return /^https?:\/\/(?:www\.)?youtube\.com\/watch/.test(url);
  };

  const constructEmbedUrl = (url: string) => {
    if (isYoutubeLink(url)) {
      const videoIdMatch = url.match(/(?:\?v=)([^#\&\?]*).*/);
      if (videoIdMatch?.[1]) {
        return `https://www.youtube.com/embed/${videoIdMatch[1]}`;
      }
    }

    return url;
  };

  const handleData = (data: SmartrecruitersProps) => {
    if (!data.active) {
      return navigateToHomeWhenIsNotActive();
    }

    if(data.customField) {
      data.customField.forEach(item => {
        if(item.fieldLabel === 'Brands') {
          data.cf_brand = item.valueLabel
        }
      })
    }    

    if (data.location?.country) {
      data.location.country = regionName.of(
        data.location.country.toUpperCase()
      ) as string;
    }

    let { videos } = data.jobAd.sections;

    if (videos?.urls.length) {
      videos.urls = videos.urls.map((url: string) => {
        return constructEmbedUrl(url);
      });
    }

    setData(data);
    setShowGDPRArray(data?.jobAd?.sections?.videos?.urls.map(() => true) || []);
    setIsLoading(false);
  };

  const fetchData = async () => {
    setIsLoading(true);
    fetch(`https://api.smartrecruiters.com/v1/companies/BayWaAG/postings/${id}`)
      .then((response) => response.json())
      .then((data) => handleData(data))
      .catch(() => {
        window.close();
        setIsLoading(false);
      });
  };

  const renderSection = (section: any, changeStrongProp?: boolean) => {
    const div = document.createElement('div');
    div.innerHTML = section.text;

    const aTags = div.querySelectorAll('a');
    aTags.forEach((aTag) => {
      aTag.setAttribute('target', '_blank');
      aTag.setAttribute('rel', 'noopener noreferrer');
    });

    return (
      <Box marginTop={5}>
        <Typography variant="h5" fontWeight={700}>
          {section.title}
        </Typography>
        <Box
          dangerouslySetInnerHTML={{ __html: div.innerHTML }}
          mt={2}
          fontSize={theme.typography.h5.fontSize}
          color={theme.palette.text.secondary}
          sx={{
            strong: {
              color: changeStrongProp ? theme.palette.text.primary : 'revert',
              fontWeight: changeStrongProp ? 500 : 'revert',
            },
            a: {
              color: theme.palette.primary.main,
            },
          }}
        />
      </Box>
    );
  };

  const navigateToHomeWhenIsNotActive = () => {
    navigate('/', { state: { showInitiative: true } });
  };

  const toggleGDPR = (index: number) => {
    const newShowGDPRArray = [...showGDPRArray];
    newShowGDPRArray[index] = false;
    setShowGDPRArray(newShowGDPRArray);
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <Box
      display="flex"
      justifyContent="center"      
      width="100%"
      minHeight="100vh"      
      paddingBottom="68px"
      className="jobsearch__detail"
    >
      {isViewLoading ? (
        <CircularProgress />
      ) : (
        <Box mt={1} width="100%">        
          <Box mt={2}>
            <Typography
              color="primary"
              variant="h3"
              letterSpacing="0.48px"
              fontWeight={500}
            >
              {data.name}
            </Typography>

            <Box
              marginY={1}
              display="flex"
              flexDirection={isMobile ? 'column' : 'row'}
              gap={isMobile ? 1 : 4}
              color={theme.palette.text.secondary}
              letterSpacing="0.28px"
            >
              {data.department && (
                <Typography variant="subtitle1" fontWeight={500}>
                  {data.department.label}
                </Typography>
              )}

              {data.cf_brand && (
                <Typography variant="subtitle1" fontWeight={500}>
                  {data.cf_brand}
                </Typography>
              )}

              {data.typeOfEmployment && (
                <Typography variant="subtitle1" fontWeight={500}>
                  {data.typeOfEmployment.label}
                </Typography>
              )}

              {data.refNumber && (
                <Typography variant="subtitle1" fontWeight={500}>
                  {t('Kennziffer')}: {data.refNumber}
                </Typography>
              )}
            </Box>

            {data.location && (
              <Typography
                variant="subtitle1"
                letterSpacing="0.28px"
                color={theme.palette.text.secondary}
                fontWeight={500}
              >
                {data.location.address}, {data.location.postalCode}{' '}
                {data.location.city}, {data.location.country}
              </Typography>
            )}
          </Box>

          <Box marginTop={5} display="flex" flexWrap="wrap" gap={2}>
            <FilledButtonStyled
              href={data.applyUrl}
              sx={{
                [device.mobile]: {
                  width: '100%',
                },
              }}
            >
              {t('Jetzt bewerben')}
            </FilledButtonStyled>
            <OutlinedButtonStyled
              href={data.referralUrl}
              sx={{
                minWidth: '219px',
                [device.mobile]: {
                  width: '100%',
                },
              }}
            >
              {t('Jemanden empfehlen')}
            </OutlinedButtonStyled>
          </Box>

          {data.jobAd && data.jobAd.sections && (
            <Box>
              {data.jobAd.sections.companyDescription &&
                renderSection(data.jobAd.sections.companyDescription, true)}
              {data.jobAd.sections.jobDescription &&
                renderSection(data.jobAd.sections.jobDescription)}
              {data.jobAd.sections.qualifications &&
                renderSection(data.jobAd.sections.qualifications)}
              {data.jobAd.sections.additionalInformation &&
                renderSection(data.jobAd.sections.additionalInformation)}
              {data.jobAd.sections.videos &&
                data.jobAd.sections.videos.urls.length && (
                  <Box sx={{maxWidth: '800px'}}>
                    {data.jobAd.sections.videos.urls.map((video, index) => (
                      <Box key={index}>
                        {showGDPRArray[index] ? (
                          <Box position="relative">
                            <Box
                              position="absolute"
                              bottom="20px"
                              zIndex="1"
                              lineHeight="28px"
                              width="100%"
                              textAlign="center"
                            >
                              <Typography
                                variant="h5"
                                color="secondary"
                                sx={{
                                  [device.small]: {
                                    fontSize:
                                      theme.typography.subtitle2.fontSize,
                                  },
                                }}
                              >
                                {t('Das Video wird von YouTube eingebettet.')}
                              </Typography>

                              <a
                                href="https://policies.google.com/privacy?hl=de"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: theme.palette.secondary.main,
                                  textDecoration: 'underline',
                                }}
                              >
                                <Typography
                                  variant="h5"
                                  color="secondary"
                                  sx={{
                                    [device.small]: {
                                      fontSize:
                                        theme.typography.subtitle2.fontSize,
                                    },
                                  }}
                                >
                                  {t(
                                    'Es gilt die Datenschutzerklärung von Google.'
                                  )}
                                </Typography>
                              </a>
                            </Box>

                            <Box
                              position="absolute"
                              top="50%"
                              left="50%"
                              zIndex="1"
                              sx={{ transform: 'translate(-50%, -50%)' }}
                              onClick={() => toggleGDPR(index)}
                            >
                              <Box
                                width="48px"
                                height="48px"
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                sx={{
                                  cursor: 'pointer',
                                  background: theme.palette.secondary.main,
                                  '&:hover': {
                                    background: theme.palette.primary.main,
                                  },
                                }}
                              >
                                <PlayArrow
                                  sx={{
                                    fontSize: '48px',
                                    '&:hover': {
                                      color: theme.palette.secondary.main,
                                    },
                                  }}
                                />
                              </Box>
                            </Box>

                            <img
                              src={Thumbnail}
                              alt="Thumbnail"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                          </Box>
                        ) : (
                          <iframe
                            src={`${video}?autoplay=1`}
                            title={data.jobAd.sections.videos.title}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                            className="jobsearch__iframe"
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
            </Box>
          )}

          <Box marginTop={5} display="flex" flexWrap="wrap" gap={2}>
            <FilledButtonStyled
              href={data.applyUrl}
              sx={{
                [device.mobile]: {
                  width: '100%',
                },
              }}
            >
              {t('Jetzt bewerben')}
            </FilledButtonStyled>
            <OutlinedButtonStyled
              href={data.referralUrl}
              sx={{
                minWidth: '219px',
                [device.mobile]: {
                  width: '100%',
                },
              }}
            >
              {t('Jemanden empfehlen')}
            </OutlinedButtonStyled>
          </Box>
        </Box>
      )}
    </Box>
  );
};
